import React, { useState, useRef } from 'react';
import { Send, Paperclip, EyeOff, X, AtSign } from 'lucide-react';
import { CreateActivityDto, ActivityMention, ActivityAttachment } from '../../types/activity';
import { Button } from '../ui/Button';

interface CommentFormProps {
  entityType: 'complaint' | 'work_order';
  entityId: string;
  currentUser: { user_id: string; name: string; role: string };
  availableUsers?: { user_id: string; name: string }[];
  onSubmit: (dto: CreateActivityDto) => void;
}

export const CommentForm: React.FC<CommentFormProps> = ({
  entityType, entityId, currentUser, availableUsers = [], onSubmit,
}) => {
  const [content, setContent] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [mentions, setMentions] = useState<ActivityMention[]>([]);
  const [attachments, setAttachments] = useState<ActivityAttachment[]>([]);
  const [showMentionList, setShowMentionList] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const filteredUsers = availableUsers.filter(u =>
    u.name.toLowerCase().includes(mentionQuery.toLowerCase()) &&
    u.user_id !== currentUser.user_id
  );

  const handleInput = (value: string) => {
    setContent(value);
    const cursorPos = textareaRef.current?.selectionStart || 0;
    const textBeforeCursor = value.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1]);
      setShowMentionList(true);
    } else {
      setShowMentionList(false);
    }
  };

  const insertMention = (user: { user_id: string; name: string }) => {
    const cursorPos = textareaRef.current?.selectionStart || 0;
    const textBeforeCursor = content.slice(0, cursorPos);
    const textAfterCursor = content.slice(cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    const newText = textBeforeCursor.slice(0, atIndex) + `@${user.name} ` + textAfterCursor;
    setContent(newText);
    setMentions(prev => [...prev, { user_id: user.user_id, user_name: user.name }]);
    setShowMentionList(false);
  };

  const handleFileAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setAttachments(prev => [...prev, {
          id: `ATT-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          file_name: file.name,
          file_type: file.type,
          data_url: reader.result as string,
          uploaded_at: new Date().toISOString(),
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeAttachment = (id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() && attachments.length === 0) return;

    onSubmit({
      entity_type: entityType,
      entity_id: entityId,
      activity_type: isInternal ? 'internal_note' : 'comment',
      content: content.trim(),
      mentions,
      attachments,
      is_internal: isInternal,
    });

    setContent('');
    setIsInternal(false);
    setMentions([]);
    setAttachments([]);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={e => handleInput(e.target.value)}
          placeholder={isInternal ? 'Write an internal note (visible only to Officers & Supervisors)...' : 'Write a comment... Use @ to mention users'}
          rows={3}
          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-sky-500 resize-none"
        />

        {showMentionList && filteredUsers.length > 0 && (
          <div className="absolute bottom-full left-0 right-0 mb-1 bg-slate-800 border border-slate-700 rounded-lg max-h-32 overflow-y-auto z-10">
            {filteredUsers.map(user => (
              <button
                key={user.user_id}
                type="button"
                onClick={() => insertMention(user)}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-200 hover:bg-slate-700 text-left"
              >
                <AtSign className="w-3 h-3 text-sky-400" />
                {user.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {attachments.map(att => (
            <div key={att.id} className="flex items-center gap-1.5 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1">
              <span className="text-[10px] text-slate-400 truncate max-w-[100px]">{att.file_name}</span>
              <button type="button" onClick={() => removeAttachment(att.id)} className="text-slate-500 hover:text-rose-400">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setIsInternal(!isInternal)}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-colors ${
              isInternal
                ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <EyeOff className="w-3 h-3" />
            Internal Note
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium bg-slate-800 text-slate-400 hover:bg-slate-700 transition-colors"
          >
            <Paperclip className="w-3 h-3" />
            Attach
          </button>
          <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileAttach} />
        </div>

        <Button type="submit" size="sm" variant="primary" icon={<Send className="w-3.5 h-3.5" />} disabled={!content.trim() && attachments.length === 0}>
          Post
        </Button>
      </div>
    </form>
  );
};
